// Compatibility shim for third-party scripts that call the iOS WKWebView
// bridge (`window.webkit.messageHandlers.*.postMessage`) without feature
// detection. Bing UET (loaded via GTM), in-app browser tooling, and browser
// extensions assume the bridge exists; on normal Safari/Chrome `window.webkit`
// is missing and the access throws (Sentry issue 125680296).
//
// On the open web there is no native handler to receive messages — a no-op
// `postMessage` is the correct behaviour. Android in-app WebViews may expose
// native handlers whose Java bridge is later GC'd; calling through them throws
// "Error invoking postMessage: Java object is gone" (Sentry issue 128469108).
// Always install the no-op proxy so third-party scripts never hit a stale bridge.
//
// Must run before third-party scripts execute — imported first in
// instrumentation-client.ts.

const NOOP_HANDLER = { postMessage: (_body: unknown) => {} }

type WebkitNamespace = {
  messageHandlers?: Record<string, { postMessage: (body: unknown) => void }>
}

type WindowWithWebkit = Window & { webkit?: WebkitNamespace }

export function installWebkitMessageHandlersShim(): void {
  if (typeof window === "undefined") return

  const w = window as WindowWithWebkit

  const handlers = new Proxy(
    {} as Record<string, { postMessage: (body: unknown) => void }>,
    {
      get(_target, prop) {
        if (prop === "then") return undefined
        return NOOP_HANDLER
      },
    }
  )

  // No host bridge (desktop Chrome, normal Safari): expose a no-op so
  // feature-detection-free callers (Bing UET, extensions) don't throw.
  if (!w.webkit) {
    w.webkit = { messageHandlers: handlers }
    return
  }

  // `window.webkit` already exists — it's a real host bridge. On Chrome for iOS
  // (a WKWebView host) `messageHandlers` is a read-only native property, so a
  // plain `w.webkit.messageHandlers = …` throws: in this strict-mode module a
  // rejected [[Set]] becomes a TypeError. Because the shim runs first in
  // instrumentation-client.ts, that throw aborts the whole client bootstrap —
  // hydration never runs and every interactive client component (navbar menu +
  // scrolled background, site search) stays dead while raw DOM taps still fire
  // (AHD-7502). `Reflect.set` returns that false [[Set]] result instead of
  // throwing, so the native bridge is left in place; it still replaces a
  // writable slot, e.g. a stale Android in-app bridge whose Java object was
  // GC'd (Sentry 128469108).
  Reflect.set(w.webkit, "messageHandlers", handlers)

  // If the assignment didn't take, `messageHandlers` is a read-only native
  // slot. On Chrome for iOS that's the real WKWebView bridge Chrome itself uses
  // (password manager, translate); leave it alone. But Android has no native
  // `window.webkit`, so when it appears there it's a fake bridge injected by an
  // in-app browser whose Java backing can be GC'd — calling through it then
  // throws "Error invoking postMessage: Java object is gone" (Sentry 133345458),
  // an Android-only failure. Redefine the fake bridge with our no-op proxy;
  // Reflect.defineProperty returns false instead of throwing on a
  // non-configurable slot, so bootstrap never aborts (AHD-7502).
  if (
    w.webkit.messageHandlers !== handlers &&
    /Android/i.test(navigator.userAgent)
  ) {
    Reflect.defineProperty(w.webkit, "messageHandlers", {
      value: handlers,
      writable: true,
      configurable: true,
    })
  }
}

installWebkitMessageHandlersShim()
